import { ISortByQuery } from '@common/api.models';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS } from '@common/constants';
import { SearchFilter } from '@common/searchFilter/SearchFilter';
import { IFilterFieldConfig, SearchFilterType } from '@common/searchFilter/searchFilter.models';
import { SearchPageBase } from '@common/SearchPageBase';
import {
  createTableDateTimeColumn,
  createTableDefaultColumn,
  createTableLinkWithOpenNewTabColumn,
  createTableUserRoleColumn,
} from '@common/tableColumn.services';
import { Column, ComponentType, Icon, IconName, isNullOrWhiteSpace, ResponsivePaginatedTable } from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import {
  useFetchCheckerCompanyOptions,
  useFetchDesignerCompanyOptions,
} from '@company/projectCompany/projectCompany.queries';
import { useFetchSrpOptionsWithSearch, useFetchTwcOptionsWithSearch } from '@userProfile/userProfile.queries';
import * as React from 'react';
import {
  EDesignModificationSearchColumnKeys,
  EDesignModificationSearchKey,
  EDesignModificationStatus,
  IProjectDesignModificationSearchFilter,
  IProjectDesignModificationSearchResult,
} from './designModification.models';
import { useFetchDesignModificationSearch } from './designModification.queries';
import { designModificationActionerOptions } from './designModificationOptions.models';
import './DesignModificationList.scss';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId, useProjectIsArchived } from '@config/config.hooks';
import { useSearchFilterUrlParam } from '@common/searchFilter/searchFilterUrlParam.hooks';
import { NumberParam, StringParam } from 'use-query-params';
import { getSelectFilterFieldNameId } from '@common/searchFilter/searchFilter.services';
import { useFetchAllRiskCategories } from '@riskCategory/riskCategory.queries';
import { useSelectT1CreationModal } from '@common/SelectT1CreationModal.hooks';
import { ESelectT1CreationModalType } from '@common/SelectT1CreationModal.constants';
import { useGetMasterScheduleOptionsForDesignModification } from '@masterSchedule/masterSchedule.queries';
import { DownloadButton } from '@file/DownloadButton';
import { useFetchReasonOfChangeOptions } from '@reasonOfChange/reasonOfChange.queries';
import { useUserProfilePreferencesPageSize } from '@userProfile/userProfilePreferences.hooks';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useAllowNewT3 } from './designModification.hooks';
import {
  exportProjectDesignModificationSearchExcelAsync,
  exportProjectDesignModificationSearchPdfAsync,
} from './designModification.services';
import '@common/Common.scss';

interface IDesignModificationListProps {}

export const DesignModificationList: React.FunctionComponent<IDesignModificationListProps> = ({}) => {
  const { initPageSize, pageSize, setPageSize } = useUserProfilePreferencesPageSize(
    EUserProfilePreferenceAttributeName.T3_LIST_PAGE_SIZE,
  );
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>({
    sortByField: EDesignModificationSearchColumnKeys.T3_NO,
    isDesc: true,
  });
  const projectId = useProjectId();
  const isProjectArchived = useProjectIsArchived();
  const { navigate, generateRoutePath, projectNo } = useNavigate();
  const isAllowNewT3 = useAllowNewT3();

  // #region Search Field Configs
  const designModificationSearchFieldConfigs = React.useMemo(() => {
    return [
      {
        fieldName: EDesignModificationSearchKey.designChangeNo,
        displayName: 'T3 No.',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.actioner,
        displayName: 'Actioner',
        type: SearchFilterType.TextSelect,
        textSelectOptions: designModificationActionerOptions,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.riskCategory,
        displayName: 'Risk Category',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchAllRiskCategories({ shouldConvertOptions: true }),
        useFetchOptionsProps: {},
      } as IFilterFieldConfig,

      {
        fieldName: EDesignModificationSearchKey.t1RefNo,
        displayName: 'T1',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,

      {
        fieldName: EDesignModificationSearchKey.designPackage,
        displayName: 'Design Package',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.location,
        displayName: 'Location',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,

      {
        fieldName: EDesignModificationSearchKey.elementOfWorks,
        displayName: 'Element of Works',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.reasonForChange,
        displayName: 'Reason For Change',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchReasonOfChangeOptions({ isDirty: false, shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.designer,
        displayName: 'Designer',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchDesignerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.checker,
        displayName: 'Checker',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchCheckerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.twc,
        displayName: 'TWC',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
        allowMultipleSelect: false,
        selectGroupOptionByKey: 'data.team.teamName',
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.twcIssue,
        displayName: 'RFC TWC',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
        allowMultipleSelect: false,
        selectGroupOptionByKey: 'data.team.teamName',
      } as IFilterFieldConfig,
      {
        fieldName: EDesignModificationSearchKey.srp,
        displayName: 'SRP',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchSrpOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
        allowMultipleSelect: false,
        selectGroupOptionByKey: 'data.team.teamName',
      } as IFilterFieldConfig,
    ];
  }, []);
  // #endregion Search Field Configs

  const { localFilter, setLocalFilter } = useSearchFilterUrlParam(
    {
      [EDesignModificationSearchKey.designChangeNo]: StringParam,
      [EDesignModificationSearchKey.t1RefNo]: StringParam,
      [EDesignModificationSearchKey.designPackage]: StringParam,
      [EDesignModificationSearchKey.elementOfWorks]: StringParam,
      [EDesignModificationSearchKey.actioner]: StringParam,
      [EDesignModificationSearchKey.location]: StringParam,
      [`${getSelectFilterFieldNameId(EDesignModificationSearchKey.riskCategory)}`]: NumberParam,
      [`${getSelectFilterFieldNameId(EDesignModificationSearchKey.checker)}`]: NumberParam,
      [`${getSelectFilterFieldNameId(EDesignModificationSearchKey.designer)}`]: NumberParam,
      [EDesignModificationSearchKey.twc]: StringParam,
      [EDesignModificationSearchKey.srp]: StringParam,
      [`${getSelectFilterFieldNameId(EDesignModificationSearchKey.reasonForChange)}`]: NumberParam,
    },
    designModificationSearchFieldConfigs,
  );

  const designModificationFilterObj = React.useMemo(() => {
    const temp = {} as any;

    Object.keys(localFilter).forEach((key) => {
      if (!key.includes('.')) temp[key] = localFilter[key];
      else {
        const splitedKey = key.split('.');
        const child = localFilter[splitedKey[0]] ?? {};
        child[splitedKey[1]] = localFilter[key];
        temp[splitedKey[0]] = child;
      }
    });

    return temp as IProjectDesignModificationSearchFilter;
  }, [JSON.stringify(localFilter)]);

  const {
    data: designModificationPagedResult,
    isLoading: isLoadingDesignModificationSearch,
    isRefetching: isRefetchingDesignModificationSearch,
    error: errorDesignModificationSearch,
  } = useFetchDesignModificationSearch({
    pageSize,
    pageNumber,
    projectId,
    ...(sortByQuery || {}),
    filter: Object.keys(localFilter).length > 0 ? (localFilter as IProjectDesignModificationSearchFilter) : undefined,
  });

  const {
    renderModal: renderDesignModificationCreationModal,
    openPortal: openDesignModificationCreationModal,
    closePortal: closeDesignModificationCreationModal,
  } = useSelectT1CreationModal({
    type: ESelectT1CreationModalType.T3,
    fetchT1OptionsFn: useGetMasterScheduleOptionsForDesignModification,
    fetchT1OptionFnProps: { projectId },
  });

  // #region Table Columns
  const designModificationTableColumns: Column[] = React.useMemo(() => {
    return [
      createTableLinkWithOpenNewTabColumn<IProjectDesignModificationSearchResult>(
        EDesignModificationSearchColumnKeys.T3_NO,
        'T3 No.',
        ({ item }) => generateRoutePath(routes.t3.view.path, { id: item.id.toString() }),
        ({ value, item, index, cellExtras }, link) => {
          let statusIcon = (
            <Icon iconName="RecordCircle" className="gcl-gems-design-modification-list-not-released-icon gcl-ml-xs" />
          );
          if (item.status === EDesignModificationStatus.cancel)
            statusIcon = (
              <Icon iconName="X" className="gcl-gems-design-modification-list-not-released-icon gcl-ml-xs" />
            );
          if (
            item.status === EDesignModificationStatus.completed ||
            item.status === EDesignModificationStatus.referToDesignerTransmittal
          )
            statusIcon = (
              <Icon iconName="CheckCircleFill" className="gcl-gems-design-modification-list-released-icon gcl-ml-xs" />
            );

          return (
            <>
              {link}
              {statusIcon}
            </>
          );
        },
      ),
      createTableDefaultColumn<IProjectDesignModificationSearchResult>(
        EDesignModificationSearchColumnKeys.ACTIONER,
        'Actioner',
        undefined,
        ({ item }) => {
          if (!isNullOrWhiteSpace(item.actioner)) {
            if (item.actioner?.trim() === '-') return 'gcl-gems-design-modification-cell no-action';
            return `gcl-gems-design-modification-cell ${item.actioner?.toLowerCase()}`;
          }

          return '';
        },
      ),
      createTableDefaultColumn(EDesignModificationSearchColumnKeys.RISK_CATEGORY, 'Risk Cat.'),
      createTableLinkWithOpenNewTabColumn<IProjectDesignModificationSearchResult>(
        EDesignModificationSearchColumnKeys.T1_REF_NO,
        'T1',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.projectMasterSchedule.id.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableDefaultColumn(EDesignModificationSearchColumnKeys.DESIGN_PACKAGE, 'Design Package'),
      createTableDefaultColumn(EDesignModificationSearchColumnKeys.LOCATION, 'Location'),
      createTableDefaultColumn(EDesignModificationSearchColumnKeys.ELEMENT_OF_WORKS, 'Element of works'),
      createTableDefaultColumn(EDesignModificationSearchColumnKeys.DESIGNER, 'Designer'),
      createTableDefaultColumn(EDesignModificationSearchColumnKeys.CHECKER, 'Checker'),
      createTableUserRoleColumn(EDesignModificationSearchColumnKeys.TWC, 'TWC'),
      createTableUserRoleColumn(EDesignModificationSearchColumnKeys.TWC_ISSUE, 'RFC TWC'),
      createTableUserRoleColumn(EDesignModificationSearchColumnKeys.SRP, 'SRP'),
      createTableDateTimeColumn(EDesignModificationSearchColumnKeys.ICE_DATE, 'ICE Approved Date'),
    ];
  }, []);
  // #endregion Table Columns
  return (
    <>
      <ApiErrorDisplay errors={[{ error: errorDesignModificationSearch, genericError: 'Failed to get T3 list' }]} />
      <SearchPageBase
        data={designModificationPagedResult?.items}
        breadcrumbItems={[
          {
            text: `${projectNo} - Home`,
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.t1.path);
            },
          },
          {
            text: `Search T3`,
            active: true,
            prefixIconName: 'Search',
          },
        ]}
        title="T3 Design Modification List"
        isTableRefetching={isRefetchingDesignModificationSearch}
        isTableLoading={isLoadingDesignModificationSearch}
        buttonNavBarItem={[
          ...(isAllowNewT3 && !isProjectArchived
            ? [
                {
                  text: 'New',
                  onClick: (e: React.MouseEvent | undefined) => {
                    openDesignModificationCreationModal(e);
                  },
                  iconName: 'FileEarmarkPlus' as IconName,
                  componentType: ComponentType.Primary,
                },
              ]
            : []),
          {
            text: 'Export',
            iconName: 'ArrowBarDown' as IconName,
            componentType: ComponentType.Secondary,
            component: (
              <DownloadButton
                exportFunction={exportProjectDesignModificationSearchExcelAsync}
                defaultExportProps={{
                  projectId,
                  sortByQuery,
                  searchFilter: designModificationFilterObj,
                }}
              />
            ),
          },
          {
            text: 'Print',
            iconName: 'Printer' as IconName,
            componentType: ComponentType.Secondary,
            component: (
              <DownloadButton
                text="Print"
                prefixIconName="Printer"
                exportFunction={exportProjectDesignModificationSearchPdfAsync}
                defaultExportProps={{
                  projectId,
                  sortByQuery,
                  searchFilter: designModificationFilterObj,
                }}
              />
            ),
          },
        ]}
        searchFilter={
          <SearchFilter
            fieldConfigs={designModificationSearchFieldConfigs}
            onFilterChanged={(filter) => {
              setLocalFilter(filter);
            }}
            defaultFilter={localFilter}
          />
        }
        appliedSearchFilter={localFilter}
        noContentLabel="No T3 created yet"
        onRenderTable={() => {
          return (
            <ResponsivePaginatedTable
              classNames={{ body: 'gcl-gems-list-table-body' }}
              columns={designModificationTableColumns}
              paginationProps={{
                pageSizeOptions: DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
                paginatorPosition: 'top',
                totalCount: designModificationPagedResult?.totalCount || 0,
                initialPageSize: initPageSize,
                onPageChange: async (pageIndex, newPageSize) => {
                  setPageNumber(pageIndex + 1);
                  setPageSize(newPageSize);
                },
              }}
              sortingProps={{
                initialSortBy:
                  sortByQuery && sortByQuery.sortByField
                    ? [
                        {
                          id: sortByQuery.sortByField,
                          desc: !!sortByQuery.isDesc,
                        },
                      ]
                    : [],
                onSort: (sortBy) => {
                  if (sortBy && sortBy.length >= 1)
                    setSortByQuery({ sortByField: sortBy[0].id, isDesc: sortBy[0].desc });
                  else setSortByQuery(undefined);
                },
              }}
              allowSelectRow={false}
              items={designModificationPagedResult?.items || []}
              allowExpandRow={false}
            />
          );
        }}
        legend={
          <ul>
            <li>
              <Icon iconName="RecordCircle" className="gcl-gems-design-modification-list-not-released-icon" /> -
              Incomplete Form
            </li>
            <li>
              <Icon iconName="CheckCircleFill" className="gcl-gems-design-modification-list-released-icon" /> -
              Completed Form
            </li>
            <li>
              <Icon iconName="X" className="gcl-gems-design-modification-list-not-released-icon" /> - Cancelled Form
            </li>
          </ul>
        }
      />
      {renderDesignModificationCreationModal()}
    </>
  );
};
